import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  AttachMoney,
  Church,
  Close,
  Delete,
  Edit,
  Save,
  Schedule,
} from "@mui/icons-material";
import apiClient from "../../utils/axios";

// ── Constantes ──────────────────────────────────────────────────────────────

const DIAS = [
  { value: 0, label: "L", title: "Lunes" },
  { value: 1, label: "M", title: "Martes" },
  { value: 2, label: "X", title: "Miércoles" },
  { value: 3, label: "J", title: "Jueves" },
  { value: 4, label: "V", title: "Viernes" },
  { value: 5, label: "S", title: "Sábado" },
  { value: 6, label: "D", title: "Domingo" },
];

const HORARIO_VACIO = {
  nombre: "",
  dias_semana: [6],
  hora_inicio: "08:00",
  hora_fin: "10:00",
  tarifa_unica: 1.00,
  horas_limite: 2.0,
  tarifa_excedido: 1.50,
  descripcion: "",
};

// ── Formulario (fuera del componente padre para evitar desmonte en cada render)

const HorarioForm = ({ horario, onChange }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          size="small"
          fullWidth
          label="Nombre"
          value={horario.nombre}
          onChange={(e) => onChange({ ...horario, nombre: e.target.value })}
          placeholder="ej. Tarifa nocturna"
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Días de la semana
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
          {DIAS.map((d) => {
            const sel = horario.dias_semana.includes(d.value);
            return (
              <Tooltip key={d.value} title={d.title}>
                <Chip
                  label={d.label}
                  size="small"
                  clickable
                  color={sel ? "primary" : "default"}
                  variant={sel ? "filled" : "outlined"}
                  onClick={() => {
                    const dias = sel
                      ? horario.dias_semana.filter((v) => v !== d.value)
                      : [...horario.dias_semana, d.value].sort((a, b) => a - b);
                    onChange({ ...horario, dias_semana: dias });
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      </Grid>

      <Grid item xs={6}>
        <TextField
          size="small"
          fullWidth
          label="Hora inicio"
          type="time"
          value={horario.hora_inicio}
          onChange={(e) => onChange({ ...horario, hora_inicio: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          size="small"
          fullWidth
          label="Hora fin"
          type="time"
          value={horario.hora_fin}
          onChange={(e) => onChange({ ...horario, hora_fin: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          size="small"
          fullWidth
          label="Tarifa base"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={horario.tarifa_unica}
          onChange={(e) =>
            onChange({ ...horario, tarifa_unica: Number(e.target.value) })
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoney fontSize="small" />
              </InputAdornment>
            ),
          }}
          helperText="Cobro hasta el límite de horas"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          size="small"
          fullWidth
          label="Límite de horas"
          type="number"
          inputProps={{ min: 0.5, step: 0.5 }}
          value={horario.horas_limite}
          onChange={(e) =>
            onChange({ ...horario, horas_limite: Number(e.target.value) })
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">h</InputAdornment>
            ),
          }}
          helperText="Horas máximas a tarifa base"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          size="small"
          fullWidth
          label="Cargo si se excede"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={horario.tarifa_excedido}
          onChange={(e) =>
            onChange({ ...horario, tarifa_excedido: Number(e.target.value) })
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoney fontSize="small" />
              </InputAdornment>
            ),
          }}
          helperText={`Ej: 2h10min → $${Number(horario.tarifa_unica||0).toFixed(2)} + 1×$${Number(horario.tarifa_excedido||0).toFixed(2)} = $${(Number(horario.tarifa_unica||0)+Number(horario.tarifa_excedido||0)).toFixed(2)}`}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          size="small"
          fullWidth
          label="Descripción (opcional)"
          value={horario.descripcion || ""}
          onChange={(e) => onChange({ ...horario, descripcion: e.target.value })}
        />
      </Grid>
    </Grid>
  </Box>
);

// ── Componente principal ─────────────────────────────────────────────────────

const HorariosMisa = ({ open, onClose }) => {
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogForm, setDialogForm] = useState({ open: false, modo: "crear", horario: HORARIO_VACIO, id: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, nombre: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [horarioVigente, setHorarioVigente] = useState(null);

  const mostrarSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const cargarHorarios = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/tarifas-horario");
      if (res?.data?.success) setHorarios(res.data.data);
    } catch {
      mostrarSnackbar("Error al cargar las tarifas por horario", "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarVigente = async () => {
    try {
      const res = await apiClient.get("/api/tarifas-horario/vigente");
      if (res?.data?.success) setHorarioVigente(res.data.en_horario_misa ? res.data.horario : null);
    } catch {
      setHorarioVigente(null);
    }
  };

  useEffect(() => {
    if (open) {
      cargarHorarios();
      cargarVigente();
    }
  }, [open]);

  const abrirCrear = () =>
    setDialogForm({ open: true, modo: "crear", horario: { ...HORARIO_VACIO }, id: null });

  const abrirEditar = (h) =>
    setDialogForm({ open: true, modo: "editar", horario: { ...h }, id: h.id });

  const cerrarForm = () =>
    setDialogForm({ open: false, modo: "crear", horario: HORARIO_VACIO, id: null });

  const guardar = async () => {
    const { modo, horario, id } = dialogForm;
    if (!horario.nombre.trim()) {
      mostrarSnackbar("El nombre es requerido", "warning");
      return;
    }
    if (!horario.dias_semana.length) {
      mostrarSnackbar("Selecciona al menos un día", "warning");
      return;
    }
    setLoading(true);
    try {
      if (modo === "crear") {
        await apiClient.post("/api/tarifas-horario", horario);
        mostrarSnackbar("Tarifa por horario creada exitosamente");
      } else {
        await apiClient.put(`/api/tarifas-horario/${id}`, horario);
        mostrarSnackbar("Tarifa por horario actualizada exitosamente");
      }
      cerrarForm();
      cargarHorarios();
      cargarVigente();
    } catch (e) {
      mostrarSnackbar(e?.response?.data?.error || "Error al guardar", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleActivo = async (h) => {
    try {
      await apiClient.put(`/api/tarifas-horario/${h.id}`, { activo: !h.activo });
      cargarHorarios();
      cargarVigente();
      mostrarSnackbar(`Horario ${!h.activo ? "activado" : "desactivado"}`);
    } catch {
      mostrarSnackbar("Error al cambiar estado", "error");
    }
  };

  const confirmarEliminar = (h) =>
    setConfirmDelete({ open: true, id: h.id, nombre: h.nombre });

  const eliminar = async () => {
    try {
      await apiClient.delete(`/api/tarifas-horario/${confirmDelete.id}`);
      mostrarSnackbar("Horario eliminado");
      setConfirmDelete({ open: false, id: null, nombre: "" });
      cargarHorarios();
      cargarVigente();
    } catch {
      mostrarSnackbar("Error al eliminar", "error");
    }
  };

  const formatDias = (dias) =>
    DIAS.filter((d) => dias.includes(d.value))
      .map((d) => d.title)
      .join(", ") || "—";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ backgroundColor: "#393939", color: "white", p: 0, position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2.5 }}>
            <Church />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Tarifas por Horario
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Durante estos horarios se cobra una tarifa única fija
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"
            sx={{ position: "absolute", top: 12, right: 12, color: "white" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Banner si hay horario vigente ahora */}
          {horarioVigente && (
            <Alert severity="info" icon={<Church />} sx={{ mb: 2 }}>
              <strong>Horario activo ahora:</strong> {horarioVigente.nombre} —{" "}
              {horarioVigente.hora_inicio}–{horarioVigente.hora_fin} · Tarifa
              única: <strong>${Number(horarioVigente.tarifa_unica).toFixed(2)}</strong>
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Horarios configurados
            </Typography>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={abrirCrear}>
              Nuevo horario
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : horarios.length === 0 ? (
            <Card variant="outlined" sx={{ borderStyle: "dashed" }}>
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Schedule sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                <Typography color="text.secondary">
                  No hay tarifas por horario configuradas
                </Typography>
                <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2 }} onClick={abrirCrear}>
                  Agregar el primero
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, backgroundColor: "#f5f5f5" } }}>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Días</TableCell>
                  <TableCell>Horario</TableCell>
                  <TableCell align="right">Tarifa única</TableCell>
                  <TableCell align="center">Activo</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {horarios.map((h) => (
                  <TableRow key={h.id} hover
                    sx={{ opacity: h.activo ? 1 : 0.5 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{h.nombre}</Typography>
                      {h.descripcion && (
                        <Typography variant="caption" color="text.secondary">{h.descripcion}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDias(h.dias_semana)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<Schedule fontSize="small" />}
                        label={`${h.hora_inicio} – ${h.hora_fin}`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color="primary">
                        ${Number(h.tarifa_unica).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={h.activo}
                        onChange={() => toggleActivo(h)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEditar(h)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => confirmarEliminar(h)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Divider sx={{ my: 2 }} />

          <Alert severity="warning" variant="outlined">
            Cuando un vehículo realiza el pago durante una franja horaria activa, se cobra
            automáticamente la <strong>tarifa única</strong> definida, ignorando la tarifa
            por hora o precio fijo configurado normalmente.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de crear / editar */}
      <Dialog open={dialogForm.open} onClose={cerrarForm} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialogForm.modo === "crear" ? "Nueva tarifa por horario" : "Editar tarifa por horario"}
        </DialogTitle>
        <DialogContent>
          <HorarioForm
            horario={dialogForm.horario}
            onChange={(h) => setDialogForm((prev) => ({ ...prev, horario: h }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrarForm} variant="outlined">Cancelar</Button>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} /> : <Save />}
            onClick={guardar} disabled={loading}
          >
            {dialogForm.modo === "crear" ? "Crear" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null, nombre: "" })}
        maxWidth="xs" fullWidth
      >
        <DialogTitle>Eliminar horario</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Deseas eliminar el horario <strong>{confirmDelete.nombre}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, id: null, nombre: "" })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={eliminar}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HorariosMisa;
